import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { PdfViewer } from 'pages/common/IPriseJobs/viewer';
import React, { useCallback, useState } from 'react';
import { useFetchList } from 'services/common/react-query-common-service';
import { MASTER_ENDPOINT } from 'utils/Constant';

import StylishLoader from '../../components/StylishLoader';
import CustomUploadFileDialog from './CustomUploadFileDialog';

interface AttachmentItemProps {
  fileName: string;
  uploadInfo: string;
  onDownload: () => void;
  onView: () => void;
}

const AttachmentItem: React.FC<AttachmentItemProps> = ({
  fileName,
  uploadInfo,
  onDownload,
  onView,
}) => (
  <Box className="attachment-card">
    <Box className="attachment-left">
      <Checkbox size="small" checked disabled className="attachment-checkbox" />
      <Box className="file-icon-wrapper">
        <Typography className="pdf-tag">PDF</Typography>
        <Box className="pdf-icon-placeholder" />
      </Box>
      <Box className="file-details">
        <Typography className="file-name">{fileName}</Typography>
        <Typography className="upload-info">{uploadInfo}</Typography>
      </Box>
    </Box>
    <Box className="attachment-right">
      <Button
        variant="outlined"
        startIcon={<LaunchIcon />}
        className="view-btn"
        onClick={onView}>
        View
      </Button>
      <Box className="download-btn-wrapper" onClick={onDownload}>
        <FileDownloadOutlinedIcon fontSize="small" />
      </Box>
    </Box>
  </Box>
);

interface CandidateAttachmentsProps {
  id: string;
  jobId: string;
  attachments: any[];
  isLoading: boolean;
  refetchAttachments: () => void;
}

const CandidateAttachments: React.FC<CandidateAttachmentsProps> = ({
  id,
  jobId,
  attachments,
  isLoading,
  refetchAttachments,
}) => {
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);

  const { data: typeResp } = useFetchList<any>(
    ['attachment-types'],
    `${String(MASTER_ENDPOINT.Candidates)}/attachments/types`,
  );

  const fileTypeDataList = typeResp?.types || [];

  const handleDownload = useCallback((attachment: any) => {
    if (!attachment?.fileId) return;
    const url = `${String(process.env.REACT_APP_APIURL)}${String(
      MASTER_ENDPOINT.Files,
    )}/${String(attachment.fileId)}`;
    const fileName =
      attachment?.attachmentType?.name ||
      attachment?.name ||
      `Overview-${String(attachment?.attachmentType?.id)}`;

    saveAs(url, fileName);
  }, []);

  const handleView = useCallback((attachment: any) => {
    setSelectedFile(attachment);
    setViewModalOpen(true);
  }, []);

  const handleCloseViewModal = useCallback(() => {
    setViewModalOpen(false);
    setSelectedFile(null);
  }, []);

  const getUserName = (user: any) => {
    if (!user) return '-';
    const firstName = String(user.firstName || '');
    const lastName = String(user.lastName || '');
    return `${firstName} ${lastName}`.trim() || '-';
  };

  return (
    <Box className="attachments-container inter-font-family">
      <Box className="action-header">
        <Button
          variant="outlined"
          startIcon={<FileUploadOutlinedIcon />}
          className="upload-btn"
          onClick={() => setUploadModalOpen(true)}>
          Upload Files
        </Button>
      </Box>

      <Box className="attachments-list">
        {isLoading ? (
          <Box display="flex" justifyContent="center">
            <StylishLoader fullPage={false} className="table" />
          </Box>
        ) : attachments.length > 0 ? (
          attachments.map((attachment: any, index: number) => {
            const fileName =
              attachment.fileName ||
              attachment.name ||
              attachment?.file?.fileName ||
              '-';
            const uploadTime = attachment.createdAt
              ? moment(attachment.createdAt).fromNow()
              : '-';
            const userName = getUserName(attachment.createdUser);
            const uploadInfo = `${uploadTime}${
              userName !== '-' ? ` by ${userName}` : ''
            }`;

            return (
              <AttachmentItem
                key={attachment.id || index}
                fileName={fileName}
                uploadInfo={uploadInfo}
                onDownload={() => handleDownload(attachment)}
                onView={() => handleView(attachment)}
              />
            );
          })
        ) : (
          <Typography className="no-records">No Records Found</Typography>
        )}
      </Box>

      {/* File View Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        maxWidth="sm"
        fullWidth
        aria-labelledby="file-view-modal-title">
        <DialogTitle
          id="file-view-modal-title"
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            padding: '10px 20px',
          }}>
          <Typography
            sx={{
              color: '#242424',
              fontFamily: 'Inter',
              fontSize: '16px',
              fontWeight: 600,
            }}>
            {selectedFile?.attachmentType?.name ||
              selectedFile?.fileName ||
              selectedFile?.name ||
              'File View'}
          </Typography>
          <IconButton onClick={handleCloseViewModal} sx={{ color: '#6b7280' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{ height: '80vh', overflow: 'hidden', padding: '0px' }}>
          {selectedFile && (
            <PdfViewer
              url={`${String(process.env.REACT_APP_APIURL)}${String(
                MASTER_ENDPOINT.Files,
              )}/${String(selectedFile.fileId)}`}
              fileName={String(
                selectedFile.attachmentType?.name || selectedFile.name || '',
              )}
              candidateId={id}
              hideDownload={false}
              lastUpdated={selectedFile.updatedAt || selectedFile.createdAt}
            />
          )}
        </DialogContent>
      </Dialog>

      <CustomUploadFileDialog
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        candidateId={id}
        jobId={jobId}
        onUploadSuccess={() => refetchAttachments()}
        attachmentTypes={fileTypeDataList}
      />
    </Box>
  );
};

export default CandidateAttachments;
